// ProjectDetail.js
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PROJECTS } from './data';
import { FiArrowLeft, FiCalendar, FiCheck, FiExternalLink } from 'react-icons/fi';
import { FaGithub, FaLinkedin } from 'react-icons/fa';
import { IoCloseOutline } from 'react-icons/io5';

const ProjectDetail = () => {
  const { id } = useParams();
  const project = PROJECTS.find((p) => p.id === id);
  const [selectedImage, setSelectedImage] = useState(null);

  if (!project) {
    return (
      <div className="min-h-screen bg-[#0a0d14] flex flex-col items-center justify-center text-white px-4">
        <h2 className="text-2xl font-bold mb-4 font-mono text-cyan-400">Project Not Found</h2>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold transition-colors duration-200"
        >
          <FiArrowLeft /> Back to Home
        </Link>
      </div>
    );
  }

  // Check if project is a mobile app project by looking at its tags
  const isMobileProject = project.tags && project.tags.some(tag => 
    ['Android', 'Android Studio', 'React Native', 'Mobile App', 'XML'].includes(tag)
  );

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  // Render the dynamic repository or LinkedIn post button
  const renderLinkButton = () => {
    if (!project.github) return null;
    
    const isLinkedIn = project.github.includes('linkedin.com');
    const icon = isLinkedIn ? <FaLinkedin className="text-[#0a66c2]" size={18} /> : <FaGithub size={18} />;
    const label = isLinkedIn ? 'View Post' : 'Repository';
    
    return (
      <motion.div variants={itemVariants}>
        <a
          href={project.github}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-slate-900 border border-white/10 hover:border-indigo-500/50 hover:bg-slate-800 text-slate-300 hover:text-white text-sm font-semibold transition-all duration-300 shadow-lg"
        >
          {icon}
          <span>{label}</span>
          <FiExternalLink size={14} className="opacity-60" />
        </a>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0d14] text-slate-100 py-12 md:py-20 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-pattern pointer-events-none opacity-40" />
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-radial-gradient pointer-events-none" />

      <div className="max-w-4xl mx-auto px-4 md:px-8 relative z-10">
        
        {/* Navigation & Header Actions */}
        <motion.div 
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white transition-colors duration-200"
          >
            <FiArrowLeft className="group-hover:-translate-x-1 transition-transform duration-200" />
            Back to Dashboard
          </Link>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Project Title & Meta */}
          <motion.div variants={itemVariants} className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
                {project.name}
              </h1>
            </div>
            
            {/* Timeline Dates */}
            <div className="flex items-center gap-2 text-sm text-slate-400 font-mono">
              <FiCalendar className="text-cyan-400" />
              <span>{project.startDate}</span>
              <span className="text-slate-600">—</span>
              <span>{project.endDate}</span>
            </div>

            {/* Tags Badges */}
            <div className="flex flex-wrap gap-2 pt-2">
              {project.tags && project.tags.map((tag, i) => (
                <span
                  key={i}
                  className="text-xs font-mono font-semibold px-3 py-1 rounded-full bg-slate-900 border border-slate-700/60 text-slate-300 shadow-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Dynamic Link Button Placement */}
          {renderLinkButton()}

          {/* Media / Screenshots Grid */}
          <motion.div variants={itemVariants} className="space-y-3">
            <h3 className="text-lg font-bold text-white tracking-wide font-mono flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
              Media & Demo Attachments
            </h3>
            <p className="text-xs text-slate-400 font-mono">
              📌 Click screenshots to view in fullscreen. Videos can be played directly.
            </p>
            
            {/* Adaptive layout based on portrait vs landscape dimensions */}
            <div className={
              isMobileProject 
                ? 'flex flex-wrap gap-6 justify-center mt-2' 
                : 'grid grid-cols-1 md:grid-cols-2 gap-6 mt-2'
            }>
              {project.screenshots.map((src, index) => {
                const isVideo = src.endsWith('.mp4');
                return (
                  <div 
                    key={index} 
                    className={
                      isMobileProject 
                        ? 'relative rounded-xl overflow-hidden border border-white/10 bg-slate-950/60 shadow-lg aspect-[9/16] max-h-[550px] w-full sm:w-[280px] flex items-center justify-center'
                        : 'relative rounded-xl overflow-hidden border border-white/10 bg-slate-950/60 shadow-lg aspect-video w-full'
                    }
                  >
                    {isVideo ? (
                      <video
                        src={src}
                        controls
                        playsInline
                        className="w-full h-full object-contain bg-slate-950"
                      />
                    ) : (
                      <div className="w-full h-full relative cursor-pointer overflow-hidden group" onClick={() => setSelectedImage(src)}>
                        <img
                          src={src}
                          alt={`Demo Attachment ${index + 1}`}
                          className="w-full h-full object-contain bg-slate-950 transform group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                          <span className="text-xs font-semibold px-3 py-1.5 bg-black/60 backdrop-blur-md border border-white/20 rounded-md text-white">
                            Enlarge
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Detailed Description */}
          <motion.div 
            variants={itemVariants}
            className="p-6 rounded-xl glass-panel border-l-4 border-l-indigo-500/80 shadow-lg space-y-3"
          >
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400 font-mono">
              Project Overview
            </h3>
            <p className="text-slate-300 leading-relaxed text-base font-normal">
              {project.description}
            </p>
          </motion.div>

          {/* Key Features & Architecture Highlights */}
          <motion.div variants={itemVariants} className="space-y-4">
            <h3 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
              Features & Engineering Highlights
            </h3>
            <ul className="grid grid-cols-1 gap-3">
              {project.features.map((feature, idx) => (
                <li 
                  key={idx} 
                  className="flex items-start gap-3 p-3.5 rounded-lg bg-slate-900/40 border border-white/5 hover:border-slate-800 hover:bg-slate-900/60 transition-colors duration-200"
                >
                  <span className="mt-0.5 p-0.5 rounded-full bg-cyan-500/10 text-cyan-400">
                    <FiCheck size={14} />
                  </span>
                  <span className="text-slate-300 text-sm md:text-base leading-relaxed">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

        </motion.div>
      </div>

      {/* Lightbox Modal (Framer Motion Animation) */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm cursor-zoom-out"
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-slate-900 border border-white/10 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors duration-200"
              title="Close Fullscreen"
            >
              <IoCloseOutline size={28} />
            </button>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative max-w-5xl max-h-[85vh] overflow-hidden rounded-xl border border-white/15 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage}
                alt="Enlarged screenshot view"
                className="w-auto h-auto max-h-[80vh] object-contain mx-auto"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectDetail;
